import _ from 'lodash';
import React, {useState, useMemo} from 'react';

import {isTablet} from '../../../breakpoints';
import {ValueOp} from '../../../util/filters';
import {Sort as QuerySort} from '../../../util/queryts';
import * as RunFeed from '../../../util/runfeed';
import * as Run from '../../../util/runs';
import {makePropsAreEqual} from '../../../util/shouldUpdate';
import * as TableCols from './dataframesTablecols';
import * as DFTreeHelpers from './dataframesUtil';
import {
  DFTableColumn,
  DFTableHoverCellCoords,
  DFTableRowInterface,
} from './DFTable';
import DFTableGrid from './DFTableGrid';
import {DFTableSortIndicatorComponent} from './DFTableSortIndicator';
import makeComp from '../../../util/profiler';

export interface DFTableGridContainerProps {
  loading: boolean; // from withRunsDataLoader
  columns: DFTableColumn[];
  fixedColumns: string[];
  rows: DFTableRowInterface[]; // from parent
  tableSettings: RunFeed.Config;
  scrollXBounds: [number, number];
  childrenPaginationMap: {
    [key: string]: number | undefined;
  }; // maps row id to the active page for each paginated group, like {groupId2 => 2, groupId2 => 5}
  columnDragAccessor?: string;
  columnDropAccessor?: string;
  columnResizingAccessor?: string;
  expandedRowAddresses: string[];
  loadingRowAddresses: string[];
  pinnedTreeRef: React.RefObject<HTMLDivElement>;
  unpinnedTreeRef: React.RefObject<HTMLDivElement>;
  expandable?: boolean;
  expanded?: boolean;
  SortIndicatorComponent?: DFTableSortIndicatorComponent;
  readOnly?: boolean;
  moveColumn(): void;
  togglePinnedColumn(columnAccessor: string): void; // add column to (or remove from) conf.columnPinned
  hideColumn(columnAccessor: string): void;
  toggleExpandedRow(rowAddress: string): void;
  setTableSettings(config: Partial<RunFeed.Config>): void;
  addFilter(key: Run.Key, op: ValueOp, value: Run.Value): void;
  updateSort(updateFn: (sort: QuerySort) => void): void;
  addGroup(newGroup: Run.Key): void;
  setChildrenPagination(rowAddress: string, activePage?: number): void;
  setTableState(stateUpdate: {
    columnDragAccessor?: string;
    columnDropAccessor?: string;
    columnResizingAccessor?: string;
  }): void;
  openSortPopup?(): void;
}

export const DFTableGridContainer: React.FC<DFTableGridContainerProps> = makeComp(
  ({
    loading,
    addGroup,
    updateSort,
    tableSettings,
    setTableSettings,
    scrollXBounds,
    childrenPaginationMap,
    setChildrenPagination,
    setTableState,
    expandedRowAddresses,
    loadingRowAddresses,
    toggleExpandedRow,
    columns,
    fixedColumns,
    rows,
    expanded,
    moveColumn,
    togglePinnedColumn,
    hideColumn,
    columnDragAccessor,
    columnDropAccessor,
    columnResizingAccessor,
    addFilter,
    pinnedTreeRef,
    unpinnedTreeRef,
    SortIndicatorComponent,
    expandable,
    readOnly,
    openSortPopup,
  }: DFTableGridContainerProps) => {
    const [hoverCellCoords, setHoverCellCoords] = useState<
      DFTableHoverCellCoords
    >([]);

    const pinnedColumns = useMemo(
      () => columns.filter(c => TableCols.isPinned(tableSettings, c.accessor)),
      [tableSettings, columns]
    );

    const unPinnedColumns = useMemo(
      () => columns.filter(c => !TableCols.isPinned(tableSettings, c.accessor)),
      [tableSettings, columns]
    );

    const pinnedColumnWidth = _.sum(
      pinnedColumns.map(c => DFTreeHelpers.getColumnWidth(tableSettings, c.key))
    );

    // Create a wrapper function so we can quickly render a pinned and unpinned table
    const renderTableBody = (opts: {
      cols: DFTableColumn[];
      pinned: boolean;
      leftMargin?: number;
    }) => {
      return (
        <DFTableGrid
          treeRef={opts.pinned ? pinnedTreeRef : unpinnedTreeRef}
          loadingTable={loading}
          childrenPaginationMap={childrenPaginationMap}
          setChildrenPagination={setChildrenPagination}
          pinned={opts.pinned}
          leftMargin={opts.leftMargin || 0}
          columns={opts.cols}
          fixedColumns={fixedColumns}
          rows={rows}
          updateSort={updateSort}
          hoverCellCoords={hoverCellCoords}
          tableSettings={tableSettings}
          SortIndicatorComponent={SortIndicatorComponent}
          setTableSettings={setTableSettings}
          setTableState={setTableState}
          setHoverCellCoords={setHoverCellCoords}
          addFilter={addFilter}
          addGroup={addGroup}
          columnDragAccessor={columnDragAccessor}
          columnDropAccessor={columnDropAccessor}
          columnResizingAccessor={columnResizingAccessor}
          expandable={expandable}
          expanded={expanded}
          readOnly={readOnly}
          moveColumn={moveColumn}
          togglePinnedColumn={togglePinnedColumn}
          hideColumn={hideColumn}
          expandedRowAddresses={expandedRowAddresses}
          loadingRowAddresses={loadingRowAddresses}
          toggleExpandedRow={toggleExpandedRow}
          scrollXBounds={opts.pinned ? [0, 10000] : scrollXBounds}
          openSortPopup={openSortPopup}
        />
      );
    };

    // When screen size is below tablet breakpoint we don't provide pinned columns behavior.
    // Render the table with normal inline scroll.
    if (isTablet()) {
      return renderTableBody({
        cols: columns,
        pinned: false,
      });
    } else {
      // Otherwise render the table with pinned columns on top
      return (
        <>
          {renderTableBody({
            cols: pinnedColumns,
            pinned: true,
          })}
          {(expandable == null || expanded) &&
            renderTableBody({
              cols: unPinnedColumns,
              pinned: false,
              leftMargin: pinnedColumnWidth,
            })}
        </>
      );
    }
  },
  {
    id: 'DFTableGridContainer',
    memo: makePropsAreEqual({
      name: 'DFTableGridContainer',
      deep: [
        'query',
        'scrollXBounds',
        'groupSelection',
        'groupTempSelection',
        'tempSelectedCount',
        'fixedColumns',
        'loadingRowAddresses',
      ],
      ignoreFunctions: true,
    }),
  }
);
